import { type IDataObjectIdentity, queryBuilder } from '@inpart-io/devtools-iap';
import { GET_BY_ID_LIST, RETRIEVE_BY_QUERY, UPDATE_DATA_OBJECT } from '@inpart-io/devtools-vue';
import { DataType, DocumentOriginValues, type IPrivateDocument } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { ADD_SEARCH_RESULT, REMOVE_SEARCH_RESULT, SAVE_SEARCH_RESULT } from '@/store/base/search/actions';

import { PRIVATE_DOCUMENT_MODULE } from '.';

export const ADD_DOCUMENTS_TO_COMMENT = 'addDocumentsToComment';
export const REMOVE_COMMENT_FROM_DOCUMENTS = 'removeDocumentsToComment';
export const RETRIEVE_DOCUMENTS_FOR_COMMENT_LIST = 'retrieveDocumentsForCommentList';

export const actions: ActionTree<IPrivateDocument, unknown> = {
    async [ADD_DOCUMENTS_TO_COMMENT](
        context,
        {
            documentIdList,
            commentId,
            properties,
            valueType = DataType.LeadComment
        }: { documentIdList: number[]; commentId: number; properties?: string; valueType: string }
    ): Promise<void> {
        const newDocuments = [];
        for (const documentId of documentIdList) {
            newDocuments.push({
                dobjId: documentId,
                links: { '@value': [{ type: valueType, dobjId: commentId, queryScore: 0 }], '@operation': 'ADD' },
                origin: DocumentOriginValues.COMMENT,
                originLink: { type: valueType, dobjId: commentId }
            });
        }
        const result = await context.dispatch(UPDATE_DATA_OBJECT, { dataObjectList: newDocuments, properties });
        return context.dispatch(ADD_SEARCH_RESULT, { key: commentId.toString(), idList: result });
    },

    async [REMOVE_COMMENT_FROM_DOCUMENTS](
        context,
        {
            documentIdList,
            commentId,
            properties,
            valueType = DataType.LeadComment
        }: { documentIdList: number[]; commentId: number; properties?: string; valueType?: string }
    ): Promise<void> {
        const newDocuments = [];
        for (const documentId of documentIdList) {
            newDocuments.push({
                dobjId: documentId,
                links: {
                    '@value': [{ type: valueType, dobjId: commentId, queryScore: 0 }],
                    '@operation': 'REMOVE'
                }
            });
        }
        const result = await context.dispatch(UPDATE_DATA_OBJECT, { dataObjectList: newDocuments, properties });
        return context.dispatch(REMOVE_SEARCH_RESULT, { key: commentId.toString(), idList: result });
    },

    async [RETRIEVE_DOCUMENTS_FOR_COMMENT_LIST](
        context,
        { commentIdList, mainLinkId, mainLinkType, properties }: { commentIdList: number[]; mainLinkId: number; mainLinkType: DataType; properties?: string }
    ): Promise<number[]> {
        const _comparators = [];
        if (mainLinkType === DataType.EvaluationBlock) {
            _comparators.push(queryBuilder.cmp.refersTo('mainLink', mainLinkId));
            _comparators.push(queryBuilder.cmp.typeIs('mainLink', DataType.Evaluation));
        } else {
            _comparators.push(queryBuilder.cmp.equalTo('mainLink', mainLinkId));
            _comparators.push(queryBuilder.cmp.typeIs('mainLink', mainLinkType));
            _comparators.push(queryBuilder.cmp.isIn('links', commentIdList));
        }
        const query = queryBuilder.build(queryBuilder.op.and(..._comparators));
        const documentIdList = await context.dispatch(RETRIEVE_BY_QUERY, {
            query,
            properties
        });
        const documentList = context.rootGetters[`${PRIVATE_DOCUMENT_MODULE}/${GET_BY_ID_LIST}`](documentIdList);
        const tempState: Record<string, number[]> = {};
        for (const document of documentList) {
            const linkedCommentIdList: string[] =
                document.links?.map((link: IDataObjectIdentity) => {
                    return link.dobjId.toString();
                }) ?? [];

            for (const linkedCommentId of linkedCommentIdList) {
                if (tempState?.[linkedCommentId] === undefined || tempState?.[linkedCommentId] === null) {
                    tempState[linkedCommentId] = [];
                }
                tempState[linkedCommentId].push(document.dobjId);
            }
        }
        for (const commentId of Object.keys(tempState)) {
            void context.dispatch(SAVE_SEARCH_RESULT, { key: commentId.toString(), idList: tempState[commentId] });
        }
        return documentIdList;
    }
};
